import { ComponentPropsWithoutRef, forwardRef, useMemo } from 'react';
import styled from '@emotion/styled';

const SelectWrapper = styled.div`
    position: relative;
`;

const Select = styled.select<{ bgColor: string; borderColor: string }>`
    width: 100%;
    padding: 10px 35px 10px 12px;
    border: 1px solid ${(props) => props.borderColor};
    border-radius: 8px;
    background-color: ${(props) => props.bgColor};
    font-size: var(--font-medium);
    cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
    appearance: none;
    -webkit-appearance: none;
    -moz-appearance: none;

    &:focus {
        outline: none;
    }
    &:not(:disabled):hover {
        border-color: var(--main-color-blue);
    }
    &:not(:disabled):focus {
        border-color: var(--main-color-blue);
    }
`;

const DropdownIcon = styled.div<{ disabled?: boolean }>`
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    pointer-events: none;
    color: ${(props) => (props.disabled ? '#afafb0' : 'var(--main-color-blue)')};
    font-size: 12px;
    font-weight: bold;
`;

interface Props extends ComponentPropsWithoutRef<'select'> {
    hasError?: boolean;
    options: { value: string; label: string }[];
    placeholder?: string;
}

/**
 * フォームのセレクトボックス
 */
export const FormSelect = forwardRef<HTMLSelectElement, Props>(({ options, ...props }, ref) => {
    const borderColor = useMemo(() => {
        if (props.disabled) return '#afafb0';
        if (props.hasError) return 'var(--warn)';
        return 'var(--main-color-blue)';
    }, [props.disabled, props.hasError]);

    const bgColor = useMemo(() => {
        if (props.disabled) return '#EBEDF7';
        if (props.hasError) return '#fce4d6';
        return 'var(--white)';
    }, [props.disabled, props.hasError]);

    const selectProps =
        props.value !== undefined ? { ...props, value: props.value || '' } : { ...props, defaultValue: '' };

    return (
        <SelectWrapper>
            <Select {...selectProps} ref={ref} borderColor={borderColor} bgColor={bgColor}>
                <option value="" disabled>
                    {props.placeholder ?? '選択してください'}
                </option>
                {options.map((option) => (
                    <option key={option.value} value={option.value}>
                        {option.label}
                    </option>
                ))}
            </Select>
            <DropdownIcon disabled={props.disabled}>▼</DropdownIcon>
        </SelectWrapper>
    );
});

FormSelect.displayName = 'FormSelect';
